import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Clock, Users, ArrowRight, Euro, FileCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { getEligibleTrainingPath } from '@/lib/trainingUrls';

const trainings = {
  fr: [
    {
      id: 'legal-design-ia',
      title: 'Legal design avec IA',
      subtitle: 'Concevoir des documents juridiques clairs et accessibles grâce à l\'intelligence artificielle',
      duration: '7h · Distanciel',
      level: 'Intermédiaire',
      audience: 'Avocats · Juristes · Notaires',
    },
    {
      id: 'ia-taches-contractuelles',
      title: 'Maîtriser l\'IA pour les tâches contractuelles',
      subtitle: 'Automatiser la rédaction, l\'analyse et la comparaison de contrats avec l\'IA',
      duration: '7h · Distanciel',
      level: 'Intermédiaire',
      audience: 'Avocats · Juristes d\'entreprise',
    },
    {
      id: 'ia-contentieux',
      title: 'Améliorer la préparation contentieuse avec l\'IA',
      subtitle: 'Optimiser la recherche jurisprudentielle, l\'analyse prédictive et la rédaction de conclusions',
      duration: '7h · Distanciel',
      level: 'Intermédiaire',
      audience: 'Avocats · Collaborateurs',
    },
    {
      id: 'ia-recherche-juridique',
      title: 'Maîtriser la recherche juridique assistée par IA',
      subtitle: 'Maîtriser les outils de recherche juridique augmentée par l\'intelligence artificielle',
      duration: '7h · Distanciel',
      level: 'Tous niveaux',
      audience: 'Avocats · Juristes · Étudiants',
    },
    {
      id: 'ia-site-web-avocat',
      title: 'Créer un site web d\'avocat avec l\'IA de A à Z',
      subtitle: 'Créer et optimiser votre présence en ligne avec les outils IA de dernière génération',
      duration: '7h · Distanciel',
      level: 'Débutant',
      audience: 'Avocats · Cabinets',
    },
    {
      id: 'ia-securite-secret-professionnel',
      title: 'Utiliser l\'IA en toute sécurité et protéger le secret professionnel',
      subtitle: 'Maîtriser les risques de l\'IA pour la confidentialité et le secret professionnel',
      duration: '7h · Distanciel',
      level: 'Tous niveaux',
      audience: 'Avocats · Juristes · Directions juridiques',
    },
  ],
  en: [
    {
      id: 'legal-design-ai',
      title: 'Legal design with AI',
      subtitle: 'Design clear and accessible legal documents using artificial intelligence',
      duration: '7h · Remote',
      level: 'Intermediate',
      audience: 'Lawyers · Counsel · Notaries',
    },
    {
      id: 'ai-contractual-tasks',
      title: 'Mastering AI for contractual tasks',
      subtitle: 'Automate contract drafting, analysis and comparison with AI',
      duration: '7h · Remote',
      level: 'Intermediate',
      audience: 'Lawyers · In-house counsel',
    },
    {
      id: 'ai-litigation',
      title: 'Improving litigation preparation with AI',
      subtitle: 'Optimize case law research, predictive analysis and brief drafting',
      duration: '7h · Remote',
      level: 'Intermediate',
      audience: 'Lawyers · Associates',
    },
    {
      id: 'ai-legal-research',
      title: 'Mastering AI-assisted legal research',
      subtitle: 'Master AI-augmented legal research tools',
      duration: '7h · Remote',
      level: 'All levels',
      audience: 'Lawyers · Counsel · Students',
    },
    {
      id: 'ai-lawyer-website',
      title: 'Build a lawyer website with AI from A to Z',
      subtitle: 'Create and optimize your online presence with cutting-edge AI tools',
      duration: '7h · Remote',
      level: 'Beginner',
      audience: 'Lawyers · Firms',
    },
    {
      id: 'ai-security-professional-secrecy',
      title: 'Using AI securely and protecting professional secrecy',
      subtitle: 'Master AI risks for confidentiality and lawyer-client privilege',
      duration: '7h · Remote',
      level: 'All levels',
      audience: 'Lawyers · Counsel · Legal departments',
    },
  ],
};

export const EligibleTrainingCatalogPage = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const catalog = fr ? trainings.fr : trainings.en;
  const basePath = fr ? '/formation-ia-juridique-eligible-financement' : '/legal-ai-training-eligible-funding';

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={fr ? "Formations éligibles financement OPCO FIF-PL CPF | Gaius" : "Funded Training Programs OPCO FIF-PL CPF | Gaius"}
        description={fr ? "Catalogue de formations IA juridiques éligibles OPCO, FIF-PL et CPF. Legal design, contrats, contentieux, recherche juridique, site web avocat, sécurité IA. Présentiel et distanciel." : "Funded legal AI training catalog. OPCO, FIF-PL and CPF eligible. Legal design, contracts, litigation, legal research, lawyer website, AI security. On-site and remote."}
        keywords={fr ? "formation ia avocat opco, formation ia fif-pl, formation ia cpf, formation juridique financement, legal design ia, secret professionnel ia" : "legal ai training funded, opco training lawyer, fif-pl ai training, cpf legal training, ai security legal"}
        canonicalUrl={fr ? "https://gaius-tech.com/formation-ia-juridique-eligible-financement" : "https://gaius-tech.com/legal-ai-training-eligible-funding"}
      />

      {/* Hero */}
      <section className="py-16 lg:py-24 bg-[hsl(var(--primary))]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-primary-foreground/70 text-sm mb-6">
            <Link to={fr ? '/formation-ia-avocat' : '/legal-ai-training-lawyers'} className="hover:text-primary-foreground">{fr ? 'Formations' : 'Training'}</Link>
            <span>/</span>
            <span>{fr ? 'Formations éligibles financement' : 'Funded training programs'}</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            <Badge className="bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30">OPCO</Badge>
            <Badge className="bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30">FIF-PL</Badge>
            <Badge className="bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30">CPF</Badge>
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold text-primary-foreground mb-4">
            {fr ? 'Formations éligibles au financement' : 'Funded training programs'}
          </h1>
          <p className="text-lg text-primary-foreground/80 max-w-3xl">
            {fr
              ? '6 formations IA juridiques éligibles OPCO, FIF-PL et CPF. Webinaires de 7h en distanciel avec pauses et pause-déjeuner. Attestation de formation incluse.'
              : '6 legal AI training programs eligible for OPCO, FIF-PL and CPF funding. 7h remote webinars with breaks and lunch break. Training certificate included.'}
          </p>
          <div className="flex flex-wrap gap-6 mt-8 text-primary-foreground/80 text-sm">
            <div className="flex items-center gap-2"><Clock className="w-4 h-4" /><span>{fr ? '7h · Webinaire avec pauses' : '7h · Webinar with breaks'}</span></div>
            <div className="flex items-center gap-2"><Euro className="w-4 h-4" /><span>{fr ? '1 159,99 € HT' : '€1,159.99 excl. VAT'}</span></div>
            <div className="flex items-center gap-2"><FileCheck className="w-4 h-4" /><span>{fr ? 'Attestation officielle' : 'Official certificate'}</span></div>
          </div>
        </div>
      </section>

      {/* IA Générative Juridique */}
      <section id="ia-generative-juridique" className="py-16 bg-secondary/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {fr ? 'Formation IA Générative Juridique' : 'Legal Generative AI Training'}
          </h2>
          <p className="text-muted-foreground mb-8">
            {fr
              ? 'Maîtrisez l\'IA générative appliquée au droit : rédaction contractuelle, recherche juridique, analyse documentaire et legal design.'
              : 'Master generative AI applied to law: contract drafting, legal research, document analysis and legal design.'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {catalog.slice(0, 3).map((training, i) => (
              <Link key={i} to={getEligibleTrainingPath(language, training.id)} className="block">
                <Card className="border-border transition-all hover:shadow-lg hover:border-primary/20 relative opacity-75 hover:opacity-100">
                  <Badge className="absolute top-3 right-3 bg-accent text-accent-foreground text-[10px] px-2 py-0.5 shadow-md z-10">
                    {fr ? 'Bientôt disponible' : 'Soon'}
                  </Badge>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs">{training.level}</Badge>
                      <span className="text-xs text-muted-foreground">{training.audience}</span>
                    </div>
                    <CardTitle className="text-lg text-foreground">{training.title}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{training.subtitle}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-1"><Clock className="w-4 h-4 text-primary" /><span>{training.duration}</span></div>
                    </div>
                    <div className="flex items-center justify-between border-t border-border pt-4">
                      <div>
                        <span className="text-lg font-bold text-foreground">1 159,99 € HT</span>
                        <span className="text-xs text-muted-foreground ml-2">{fr ? 'distanciel' : 'remote'}</span>
                      </div>
                      <Button size="sm" className="pointer-events-none">
                        {fr ? 'Voir le programme' : 'View program'}
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Prompt Engineering Juridique */}
      <section id="prompt-engineering-juridique" className="py-16 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {fr ? 'Formation Prompt Engineering Juridique' : 'Legal Prompt Engineering Training'}
          </h2>
          <p className="text-muted-foreground mb-8">
            {fr
              ? 'Apprenez à formuler des prompts efficaces pour les LLM juridiques : recherche augmentée, sécurité des données et secret professionnel.'
              : 'Learn to craft effective prompts for legal LLMs: augmented research, data security and professional secrecy.'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {catalog.slice(3).map((training, i) => (
              <Link key={i} to={getEligibleTrainingPath(language, training.id)} className="block">
                <Card className="border-border transition-all hover:shadow-lg hover:border-primary/20 relative opacity-75 hover:opacity-100">
                  <Badge className="absolute top-3 right-3 bg-accent text-accent-foreground text-[10px] px-2 py-0.5 shadow-md z-10">
                    {fr ? 'Bientôt disponible' : 'Soon'}
                  </Badge>
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs">{training.level}</Badge>
                      <span className="text-xs text-muted-foreground">{training.audience}</span>
                    </div>
                    <CardTitle className="text-lg text-foreground">{training.title}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{training.subtitle}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-1"><Clock className="w-4 h-4 text-primary" /><span>{training.duration}</span></div>
                    </div>
                    <div className="flex items-center justify-between border-t border-border pt-4">
                      <div>
                        <span className="text-lg font-bold text-foreground">1 159,99 € HT</span>
                        <span className="text-xs text-muted-foreground ml-2">{fr ? 'distanciel' : 'remote'}</span>
                      </div>
                      <Button size="sm" className="pointer-events-none">
                        {fr ? 'Voir le programme' : 'View program'}
                        <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">{fr ? 'Besoin d\'un conseil personnalisé ?' : 'Need personalized advice?'}</h2>
          <p className="text-muted-foreground mb-8">
            {fr
              ? 'Nos conseillers vous accompagnent dans le choix de votre formation et les démarches de financement OPCO, FIF-PL ou CPF.'
              : 'Our advisors help you choose the right training and handle OPCO, FIF-PL or CPF funding paperwork.'}
          </p>
          <Button asChild size="lg"><Link to="/contact">{fr ? 'Nous contacter' : 'Contact us'}</Link></Button>
        </div>
      </section>
    </div>
  );
};
